import React, { useEffect, useState } from 'react';
import { Alert, Button, Descriptions, Form, Input, notification, Result, Skeleton, Switch, Tag } from 'antd';
import { AxiosError } from 'axios';
import styled from 'styled-components';

import { ReloadOutlined } from '@ant-design/icons';

import { handleFormErrors } from '../../../common/errorHelpers';
import { DetailsPageHeader } from '../../../components/DetailsPageHeader';
import { MatchingPageLayout } from '../matching.styles';
import { formatMatchingTime, processingReason } from '../matchingPresentation';

import { MatcherStageActivity } from './MatcherStageActivity';
import { MatcherStatus, MatchingSettings } from './matchingAdmin.types';
import { useMatchingAdmin } from './useMatchingAdmin';

const AdminLayout = styled(MatchingPageLayout)`
  section {
    margin-top: 28px;
    padding-top: 24px;
    border-top: 1px solid #dce4ed;
  }
  .matching-admin-fields {
    display: flex;
    flex-wrap: wrap;
    gap: 0 24px;
    align-items: flex-start;
  }
  .matching-admin-fields .ant-form-item {
    width: 220px;
    max-width: 100%;
  }
  @media (max-width: 575px) {
    .matching-admin-fields .ant-form-item {
      width: 100%;
    }
  }
`;

const stateLabels: Record<MatcherStatus['state'], { label: string; color?: string }> = {
  OFF: { label: 'Off' },
  STOPPING: { label: 'Stopping', color: 'gold' },
  BLOCKED: { label: 'Blocked', color: 'red' },
  SCANNING: { label: 'Scanning', color: 'blue' },
  PROCESSING: { label: 'Processing', color: 'blue' },
  IDLE: { label: 'Idle', color: 'green' },
};

export const MatchingAdminPage = () => {
  const { status, save } = useMatchingAdmin();
  const { data } = status;
  const [form] = Form.useForm<MatchingSettings>();
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (data && !dirty && !save.isLoading) form.setFieldsValue(data.settings);
  }, [data, dirty, form, save.isLoading]);

  const submit = async (values: MatchingSettings) => {
    try {
      const settings = await save.mutateAsync(values);
      form.setFieldsValue(settings);
      setDirty(false);
      notification.success({ message: 'Matching settings saved' });
    } catch (error) {
      handleFormErrors({ error: error as AxiosError, form });
    }
  };

  const denied = status.error?.response?.status === 403 || save.error?.response?.status === 403;
  if (denied) return <Result status="403" title="Administrator access required" />;
  const state = data && stateLabels[data.state];
  return (
    <AdminLayout>
      <DetailsPageHeader
        titleLevel={1}
        title="Matching"
        actions={
          <Button
            icon={<ReloadOutlined aria-hidden="true" />}
            loading={status.isFetching}
            disabled={save.isLoading}
            onClick={() => status.refetch()}
          >
            Refresh
          </Button>
        }
      />
      <p className="matching-muted">Control automatic matching and inspect its activity.</p>
      {status.error && status.error.response?.status !== 401 && (
        <Alert
          type="error"
          showIcon
          message="Status could not be refreshed"
          description={
            data ? 'The last snapshot is shown. Refresh to try again.' : 'Refresh to load the matcher status.'
          }
        />
      )}
      {status.isLoading && <Skeleton active paragraph={{ rows: 8 }} />}
      {data && state && (
        <>
          <section aria-label="Matcher activity">
            <h2>
              Activity <Tag color={state.color}>{state.label}</Tag>
            </h2>
            {data.blockedReason && <Alert type="warning" showIcon message={processingReason(data.blockedReason)} />}
            {data.state === 'STOPPING' && <p>Active work is finishing. No new work will start.</p>}
            {data.settings.enabled && data.reconciliationRequested && (
              <p className="matching-muted">A fresh scan is requested.</p>
            )}
            <MatcherStageActivity stages={data.stages} />
            <p className="matching-muted">
              Successful quick checks include rejected pairs. Scanning and processing can run together.
            </p>
            <Descriptions size="small" column={{ xs: 1, md: 3 }}>
              <Descriptions.Item label="Last automatic scan started">
                {formatMatchingTime(data.lastScanStartedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Last automatic scan finished">
                {formatMatchingTime(data.lastScanFinishedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Last task completed">
                {formatMatchingTime(data.lastWorkCompletedAt)}
              </Descriptions.Item>
            </Descriptions>
          </section>
          <section aria-label="Matching settings">
            <h2>Settings</h2>
            <p className="matching-muted">
              Changes take effect when saved. Switching off lets active calls finish and preserves stored assessments.
            </p>
            <Form
              form={form}
              layout="vertical"
              onValuesChange={() => setDirty(true)}
              onFinish={submit}
              disabled={save.isLoading}
            >
              <div className="matching-admin-fields">
                <Form.Item name="enabled" label="Automatic matching" valuePropName="checked">
                  <Switch checkedChildren="On" unCheckedChildren="Off" />
                </Form.Item>
                <Form.Item
                  name="candidateCreatedFrom"
                  label="Candidates created from"
                  rules={[{ required: true, message: 'Choose a date' }]}
                >
                  <Input type="date" />
                </Form.Item>
                <Form.Item
                  name="projectCreatedFrom"
                  label="Projects created from"
                  rules={[{ required: true, message: 'Choose a date' }]}
                >
                  <Input type="date" />
                </Form.Item>
              </div>
              <p className="matching-muted">
                Dates include the selected day in Europe/Warsaw. Earlier dates expand automatic processing; later dates
                narrow it. Existing assessments are kept.
              </p>
              {save.isError && save.error.response?.status !== 401 && (
                <Alert
                  type="error"
                  showIcon
                  message="Settings could not be saved"
                  description="Your changes are still here. Check the fields and try again."
                />
              )}
              <Button type="primary" htmlType="submit" loading={save.isLoading} disabled={!dirty}>
                Save changes
              </Button>
            </Form>
          </section>
          <section aria-label="Environment configuration">
            <h2>Configuration</h2>
            <p className="matching-muted">Read-only. Change these values through environment configuration.</p>
            <Descriptions size="small" column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label="Reconciliation interval">
                {data.reconcileIntervalSeconds.toLocaleString()} seconds
              </Descriptions.Item>
              <Descriptions.Item label="Concurrency">{data.concurrency}</Descriptions.Item>
            </Descriptions>
          </section>
          <p className="matching-muted" style={{ marginTop: 24 }}>
            Last refreshed: {formatMatchingTime(data.checkedAt)}
          </p>
        </>
      )}
    </AdminLayout>
  );
};
